import type { AuthUser } from '@/chassis/core/auth/AuthProvider';
import type { PortalDashboardOverview } from '../MyDashboard/service';

const DEFAULT_FALLBACK = 'Usuário';

export const PORTAL_DISPLAY_NAME_STORAGE_KEY = 'cdcb.portal.userDisplayName';
export const PORTAL_DISPLAY_NAME_EVENT = 'cdcb:portalDisplayNameUpdated';

function normalizeCandidate(value?: string | null): string | null {
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

export interface ResolvePortalDisplayNameArgs {
    storedName?: string | null;
    overview?: PortalDashboardOverview | null;
    overviewName?: string | null;
    authUser?: AuthUser | null;
    fallback?: string;
}

export function resolvePortalDisplayName({
    storedName,
    overview,
    overviewName,
    authUser,
    fallback = DEFAULT_FALLBACK,
}: ResolvePortalDisplayNameArgs): string {
    const fromStorage = normalizeCandidate(storedName);
    if (fromStorage) {
        return fromStorage;
    }

    const candidateOverviewName = overviewName ?? overview?.user?.name;
    const fromOverview = normalizeCandidate(candidateOverviewName);
    if (fromOverview) {
        return fromOverview;
    }

    const fromAuthName = normalizeCandidate(authUser?.name);
    if (fromAuthName) {
        return fromAuthName;
    }

    const fromEmail = normalizeCandidate(authUser?.email);
    if (fromEmail) {
        return fromEmail;
    }

    return fallback;
}

export function readStoredPortalDisplayName(): string | null {
    if (typeof window === 'undefined') {
        return null;
    }
    const raw = window.localStorage.getItem(PORTAL_DISPLAY_NAME_STORAGE_KEY);
    return normalizeCandidate(raw);
}

export function persistPortalDisplayName(value: string | null | undefined) {
    if (typeof window === 'undefined') {
        return;
    }

    const normalized = normalizeCandidate(value);

    if (normalized) {
        window.localStorage.setItem(PORTAL_DISPLAY_NAME_STORAGE_KEY, normalized);
        window.dispatchEvent(new CustomEvent<string | null>(PORTAL_DISPLAY_NAME_EVENT, { detail: normalized }));
        return;
    }

    window.localStorage.removeItem(PORTAL_DISPLAY_NAME_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent<string | null>(PORTAL_DISPLAY_NAME_EVENT, { detail: null }));
}
